import { Tile, isHonor, tileNumber } from '@/engine/tiles';

/** 수패 종류별 인쇄 글자(실제 패의 각인). */
const SUIT_MARK = ['萬', '筒', '索']; // 만 통 삭
/** 자패 글자 27..33: 東南西北白發中 */
const HONOR_CHAR = ['東', '南', '西', '北', '白', '發', '中'];
/** 자패 한글 라벨(학습용). */
const HONOR_LABEL = ['동', '남', '서', '북', '백', '발', '중'];

export type SuitKey = 'man' | 'pin' | 'sou' | 'wind' | 'haku' | 'hatsu' | 'chun';

export interface TileFace {
  number: string; // 수패: '1'..'9'; 자패: ''
  mark: string; // 수패 각인 글자 또는 자패 글자
  label: string; // 보조 라벨(학습용)
  suit: SuitKey;
}

export function tileFace(t: Tile): TileFace {
  if (isHonor(t)) {
    const i = t - 27;
    const suit: SuitKey = i < 4 ? 'wind' : (['haku', 'hatsu', 'chun'][i - 4] as SuitKey);
    return { number: '', mark: HONOR_CHAR[i], label: HONOR_LABEL[i], suit };
  }
  const suitIdx = Math.floor(t / 9); // 0 man, 1 pin, 2 sou
  const suit: SuitKey = (['man', 'pin', 'sou'] as const)[suitIdx];
  const n = tileNumber(t);
  return { number: String(n), mark: SUIT_MARK[suitIdx], label: `${n}${['만', '통', '삭'][suitIdx]}`, suit };
}

export interface RenderTileOptions {
  size?: 'sm' | 'md' | 'lg';
  faceDown?: boolean;
  rotated?: boolean; // 리치 선언 패(옆으로 눕힘)
  dora?: boolean; // 도라 강조
  dim?: boolean; // 비활성/지나간 패
  selected?: boolean;
  onClick?: (t: Tile) => void;
  title?: string;
}

/** 한 장의 타일을 DOM 요소로 렌더. CSS 프레임 + 각인 글자(유니코드 CJK). */
export function renderTile(t: Tile, opts: RenderTileOptions = {}): HTMLElement {
  const el = document.createElement(opts.onClick ? 'button' : 'div');
  el.className = 'tile';
  el.dataset.size = opts.size ?? 'md';
  if (opts.faceDown) el.classList.add('tile--back');
  if (opts.rotated) el.classList.add('tile--riichi');
  if (opts.dora) el.classList.add('tile--dora');
  if (opts.dim) el.classList.add('tile--dim');
  if (opts.selected) el.classList.add('tile--selected');

  if (opts.faceDown) {
    el.setAttribute('aria-label', '뒷면 패');
    return el;
  }

  const face = tileFace(t);
  el.classList.add(`suit-${face.suit}`);
  el.setAttribute('aria-label', face.label || face.mark);
  el.title = opts.title ?? (face.label || face.mark);

  if (face.number) {
    const num = document.createElement('span');
    num.className = 'tile__num';
    num.textContent = face.number;
    el.appendChild(num);
    const mark = document.createElement('span');
    mark.className = 'tile__mark';
    mark.textContent = face.mark;
    el.appendChild(mark);
  } else {
    const big = document.createElement('span');
    big.className = 'tile__honor';
    big.textContent = face.mark;
    el.appendChild(big);
  }

  if (opts.onClick) {
    (el as HTMLButtonElement).addEventListener('click', () => opts.onClick!(t));
  }
  return el;
}

/** 여러 장을 한 줄로. */
export function renderTileRow(tiles: Tile[], opts: RenderTileOptions = {}): HTMLElement {
  const row = document.createElement('div');
  row.className = 'tile-row';
  for (const t of tiles) row.appendChild(renderTile(t, opts));
  return row;
}
